import type { GuestMe } from '@karozik/shared';
import { Artwork, Avatar, cn, useNow, type PartyConnection } from '@karozik/ui';
import { useState } from 'react';

const CHOICE_STYLES = ['bg-pink', 'bg-violet', 'bg-cyan text-night', 'bg-sun text-night'];
const LETTERS = ['A', 'B', 'C', 'D'];

/** Full-screen blind test answer pad. Shown only while a blind test runs. */
export function BlindtestOverlay({ conn, me, big }: { conn: PartyConnection; me: GuestMe | null; big: boolean }) {
  const now = useNow(250) + conn.clockSkew;
  const bt = conn.state?.blindtest;
  const [error, setError] = useState<string | null>(null);
  if (!bt) return null;
  const left = Math.min(bt.stage === 'playing' ? 20 : 99, Math.max(0, Math.ceil((bt.stageEndsAt - now) / 1000)));
  const answered = !!me && bt.answered.includes(me.id);
  const myPoints = me ? bt.lastPoints[me.id] : undefined;
  const myRank = me ? bt.leaderboard.findIndex((e) => e.guest.id === me.id) : -1;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-night px-5 pt-6 safe-bottom">
      <div className={cn('mx-auto flex w-full flex-1 flex-col', big ? 'max-w-3xl' : 'max-w-md')}>
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-bold text-gradient">Blind test</div>
          <div className="text-sm text-ink-dim">
            {bt.round > 0 && `Manche ${bt.round}/${bt.totalRounds}`}
          </div>
        </div>

        {bt.stage === 'intro' && (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div className="text-7xl">🎧</div>
            <p className="mt-4 text-xl font-semibold">Écoute l’extrait et trouve le titre, le plus vite possible !</p>
            <div className="font-display mt-6 text-7xl font-bold">{left}</div>
          </div>
        )}

        {bt.stage === 'playing' && bt.choices && (
          <div className="flex flex-1 flex-col">
            <div className="my-4 h-2 overflow-hidden rounded-full bg-surface-2">
              <div className="bg-gradient-neon h-full transition-all duration-300" style={{ width: `${(left / 20) * 100}%` }} />
            </div>
            {answered ? (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <div className="text-6xl">🤞</div>
                <p className="mt-3 text-xl font-semibold">Réponse enregistrée !</p>
                <p className="text-ink-dim">Regarde l’écran géant…</p>
              </div>
            ) : (
              <div className="grid flex-1 grid-rows-4 gap-3 pb-4">
                {bt.choices.map((label, i) => (
                  <button
                    key={i}
                    disabled={!me}
                    onClick={async () => {
                      const r = await conn.send('blindtest:answer', { choice: i });
                      setError(r.ok ? null : (r.error ?? null));
                    }}
                    className={cn('flex items-center gap-3 rounded-2xl px-4 text-left font-bold shadow-lg transition active:scale-95', CHOICE_STYLES[i], big ? 'text-2xl' : 'text-base')}
                  >
                    <span className="font-display text-2xl opacity-70">{LETTERS[i]}</span>
                    <span className="leading-snug">{label}</span>
                  </button>
                ))}
              </div>
            )}
            {error && <p className="pb-3 text-center text-danger">{error}</p>}
          </div>
        )}

        {bt.stage === 'reveal' && (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            {bt.revealTrack && <Artwork track={bt.revealTrack} className="h-40 w-40" />}
            <div className="font-display mt-4 text-2xl font-bold">{bt.revealTrack?.title}</div>
            <div className="text-ink-dim">{bt.revealTrack?.artist}</div>
            <div className={cn('mt-6 rounded-2xl px-6 py-3 text-xl font-bold', myPoints ? 'bg-lime text-night' : 'bg-surface-2')}>
              {myPoints ? `+${myPoints} points 🎉` : answered ? 'Raté… 😅' : 'Pas de réponse'}
            </div>
          </div>
        )}

        {bt.stage === 'final' && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <div className="text-6xl">🏆</div>
            <h2 className="font-display mt-2 text-2xl font-bold">Classement</h2>
            <ol className="mt-4 w-full space-y-2">
              {bt.leaderboard.slice(0, 5).map((e, i) => (
                <li key={e.guest.id} className={cn('flex items-center gap-3 rounded-2xl bg-surface px-4 py-3', e.guest.id === me?.id && 'ring-2 ring-lime')}>
                  <span className="w-6 font-bold">{['🥇', '🥈', '🥉'][i] ?? i + 1}</span>
                  <Avatar guest={e.guest} size="sm" />
                  <span className="flex-1 truncate font-semibold">{e.guest.pseudo}</span>
                  <span className="font-bold">{e.points}</span>
                </li>
              ))}
            </ol>
            {myRank > 4 && <p className="mt-4 text-ink-dim">Tu es {myRank + 1}ᵉ !</p>}
          </div>
        )}
      </div>
    </div>
  );
}
